import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Card from 'components/Card';
import FormInput from 'components/FormInput';
import FormPhoneInput from 'components/FormPhoneInput';
import FormSelect from 'components/FormSelect';
import { ProfileImageIcon, UploadIcon, LockIcon } from 'utils/icons';

import './styles.scss';
import Button from 'components/Button';
import { useAppContext } from 'contexts/AppContext';
import { useAccountSettingsContext } from 'layouts/AccountSettingsLayout/contexts/AccountSettingsContext';
import {
  compareFormValues,
  prepareContactInfo,
  prepareProfileInfo,
} from 'utils/common';
import UploadFile from 'components/UploadFile';
import { toastStyle } from 'utils/helper';
import ChangeEmailModal from './ChangeEmailModal';
import { currencyOptions, normalizeCurrencyCode } from 'utils/currencies';
//import { toastStyle } from 'utils/helper';

const Profile = () => {
  const phoneInputKey = useRef(new Date().valueOf());
  const [uploadStatus, setUploadStatus] = useState({
    uploading: false,
    percentCompleted: 0,
  });
  const [editMode, setEditMode] = useState(false);
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);
  const { userData, userProfile, isFetchingProfile, refetchProfile } =
    useAppContext();
  const { userProfileMutation } = useAccountSettingsContext();

  const profileInfo = useMemo(
    () => prepareProfileInfo(userProfile, userData),
    [userProfile, userData]
  );
  const contactInfo = useMemo(() => {
    const contactData = prepareContactInfo(userProfile, userData);
    if (contactData?.phone_number) {
      phoneInputKey.current = new Date().valueOf();
    }
    return contactData;
  }, [userProfile, userData]);

  const {
    register: profileFormRegister,
    handleSubmit: profileFormSubmit,
    formState: { errors: profileFormErrors },
    watch: profileFormWatch,
    reset: resetProfileForm,
  } = useForm({ mode: 'onChange' });
  const profileValues = profileFormWatch();

  const {
    handleSubmit: contactFormSubmit,
    formState: { errors: contactFormErrors },
    reset: resetContactForm,
    watch: contactFormWatch,
    control: contactFormControl,
  } = useForm({ mode: 'onChange' });
  const contactValues = contactFormWatch();

  const resetForms = useCallback(() => {
    resetContactForm(contactInfo);
    resetProfileForm(profileInfo);
    setEditMode(false);
    phoneInputKey.current = new Date().valueOf();
  }, [contactInfo, profileInfo, resetContactForm, resetProfileForm]);

  useEffect(() => {
    !isFetchingProfile && resetForms();
  }, [isFetchingProfile, resetForms]);

  const enableEditMode = () => {
    // Re-apply saved values to the forms so registered inputs that mount for
    // the first time (read-only view doesn't register them) get the DOM
    // values pushed via react-hook-form's refs.
    resetContactForm(contactInfo);
    resetProfileForm(profileInfo);
    setEditMode(true);
  };

  const profileModified = useMemo(
    () =>
      profileValues ? compareFormValues(profileValues, profileInfo) : false,
    [profileValues, profileInfo]
  );

  const contactModified = useMemo(
    () =>
      contactValues ? compareFormValues(contactValues, contactInfo) : false,
    [contactValues, contactInfo]
  );

  const hasChanges = useMemo(() => {
    return profileModified || contactModified;
  }, [profileModified, contactModified]);

  const handleOnSave = async () => {
    let profileChanges = null;

    profileModified &&
      (await profileFormSubmit(() => {
        profileChanges = {
          name: profileValues?.name,
          job_title: profileValues?.job_title,
          user_type: profileValues?.user_type?.value,
        };
      })());

    let contactChanges = null;
    contactModified &&
      (await contactFormSubmit(() => {
        contactChanges = {
          userprofile: {
            phone_number: contactValues?.phone_number,
            default_currency: normalizeCurrencyCode(
              contactValues?.default_currency?.value ||
                contactValues?.default_currency
            ),
          },
        };
      })());

    if (
      !(
        (!profileModified || profileChanges) &&
        (!contactModified || contactChanges)
      )
    )
      return false;

    const userInfo = {
      id: userProfile?.id,
      ...profileChanges,
      ...contactChanges,
    };

    userProfileMutation.mutate(userInfo, {
      onSuccess: () => {
        refetchProfile?.();
        setEditMode(false);
        toast.success('Profile updated successfully', toastStyle);
      },
      onError: error => {
        const message =
          error?.response?.data?.error ||
          error?.response?.data?.message?.non_field_errors?.[0] ||
          error?.response?.data?.non_field_errors?.[0];
        toast.error(
          message ||
            'An error occurred while updating the profile. Please try again later.',
          toastStyle
        );
      },
    });
  };

  const handleUploadFile = file => {
    userProfileMutation.mutate(
      {
        profile_image: file,
        id: userProfile?.id,
        setUploadStatus,
        headers: {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data',
          },
        },
      },
      {
        onSuccess: () => {
          refetchProfile?.();
          toast.success('Profile image uploaded successfully', toastStyle);
          setUploadStatus({
            uploading: false,
            percentCompleted: 0,
          });
        },
        onError: () => {
          toast.error(
            'An error occurred while uploading the profile image. Please try again later.',
            toastStyle
          );
          setUploadStatus({
            uploading: false,
            percentCompleted: 0,
          });
        },
      }
    );
  };

  return (
    <div className="user-profile content-section">
      <h2 className="user-profile__header content-header">
        Profile
        <div className="user-profile__header__actions">
          {editMode ? (
            <>
              <Button
                label="Save"
                type="button"
                onClick={handleOnSave}
                className="user-profile-save-btn"
                variant="primary"
                disabled={!hasChanges}
              />
              <Button
                label="Cancel"
                type="reset"
                onClick={resetForms}
                className="user-profile-cancel-btn"
                variant="red-outlined"
              />
            </>
          ) : (
            <Button
              label="Edit"
              type="button"
              onClick={enableEditMode}
              className="user-profile-edit-btn"
              variant="primary"
            />
          )}
        </div>
      </h2>
      <div className="user-profile__content content-body row">
        <div className="user-profile__content__col col-md-6">
          <Card className="personal-info" title="Personal Details">
            <FormInput
              label="Full Name"
              type="text"
              placeholder="Please enter your full name"
              error={profileFormErrors?.name?.message}
              {...profileFormRegister('name', {
                required: 'Name is required',
              })}
              readOnly={!editMode}
            />
            <FormInput
              label="Job Title"
              type="text"
              placeholder="Please enter your job title"
              error={profileFormErrors?.job_title?.message}
              {...profileFormRegister('job_title', { required: false })}
              readOnly={!editMode}
            />
          </Card>
          <Card className="profile-image" title="Profile Image">
            <UploadFile
              handleUploadFile={handleUploadFile}
              uploadStatus={uploadStatus}
            >
              <div className="profile-image__content">
                <div className="profile-image__content__thumbnail">
                  {userProfile?.userprofile?.profile_image ? (
                    <img
                      src={userProfile?.userprofile?.profile_image}
                      alt="profile-pic"
                    />
                  ) : (
                    <ProfileImageIcon />
                  )}
                </div>
                <div className="profile-image__content__upload">
                  <Button
                    label={
                      <span className="upload-label">
                        Upload Image
                        <UploadIcon />
                      </span>
                    }
                    type="reset"
                    className="profile-image-upload-button"
                    variant="red-outlined"
                  />
                  {!userProfile?.userprofile?.profile_image && (
                    <div className="no-image-text">
                      No image has been uploaded
                    </div>
                  )}
                </div>
              </div>
            </UploadFile>
          </Card>
        </div>
        <div className="user-profile__content__col col-md-6">
          <Card className="contact-info" title="Contact Details">
            <FormInput
              label="Email"
              type="email"
              placeholder="Please enter your email"
              readOnly={true}
              disabled={true}
              helpText={
                <div className="help-text">
                  <LockIcon className="help-text__icon" />
                  <button
                    type="button"
                    className="help-text__content help-text__link-button"
                    onClick={() => setShowChangeEmailModal(true)}
                  >
                    Change email
                  </button>
                </div>
              }
              value={contactInfo?.email}
            />
            <FormPhoneInput
              label="Phone"
              type="text"
              placeholder="(555) 123-5555"
              error={contactFormErrors?.phone_number?.message}
              control={contactFormControl}
              name="phone_number"
              disabled={!editMode}
              rules={{
                required: 'Phone is required',
                validate: value => {
                  if (!editMode) {
                    return true;
                  }
                  const numberParts = value?.split(' ') || [];
                  const number = numberParts
                    ?.slice(1)
                    ?.join('')
                    ?.trim()
                    ?.replace(/\D/, '');

                  if (!number?.trim()) {
                    return 'Phone is required';
                  }
                  if (number?.length < 8) {
                    return 'Please enter valid phone number';
                  }
                  return true;
                },
              }}
              defaultValue={contactInfo?.phone_number || ''}
            />
            <FormSelect
              label="Default Currency"
              placeholder="Select a currency"
              options={currencyOptions}
              error={contactFormErrors?.default_currency?.message}
              name="default_currency"
              rules={{
                required: false,
              }}
              control={contactFormControl}
              isSearchable
              isDisabled={!editMode}
            />
          </Card>
        </div>
      </div>
      <ChangeEmailModal
        show={showChangeEmailModal}
        onHide={() => setShowChangeEmailModal(false)}
      />
    </div>
  );
};

export default Profile;
